import { type ApolloError } from '@apollo/client/errors';
import { type SupplierListItem } from '@choco-admin/types';

import { useGetSupplierDetailsDataQuery } from '../../../generated';
import { mapSupplierToProfileItem } from '../mappers/supplierMapper';

interface UseSupplierDetailsParams {
  id: string;
}

interface UseSupplierDetailsResult {
  supplier: SupplierListItem | null;
  isLoading: boolean;
  error: ApolloError | undefined;
  refetch: () => Promise<any>;
}

export const useSupplierDetails = ({
  id,
}: UseSupplierDetailsParams): UseSupplierDetailsResult => {
  const {
    data,
    loading: isLoading,
    error,
    refetch,
  } = useGetSupplierDetailsDataQuery({
    variables: { id },
    skip: !id,
    fetchPolicy: 'cache-and-network',
    // Downgrade after the first fetch so a cache write from an unrelated
    // query doesn't force a network refetch here unless the cache read is
    // genuinely incomplete (see the ProfileImage merge policy in
    // apollo-client.ts for the specific bug this used to trigger).
    nextFetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const mappedSupplier = data?.supplier
    ? mapSupplierToProfileItem(data.supplier)
    : null;

  return {
    supplier: mappedSupplier,
    isLoading,
    error: error as ApolloError | undefined,
    refetch,
  };
};
